import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../api';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function CatalogPdfView() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [spreadIndex, setSpreadIndex] = useState(0);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    api.get(`/api/pages/slug/${slug}`)
      .then(res => setPage(res.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const pdfUrl = page?.catalog_pdf
    ? (page.catalog_pdf.startsWith('http') ? page.catalog_pdf : `${process.env.REACT_APP_API_URL || ''}${page.catalog_pdf}`)
    : null;

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (err) => {
    setPdfError(err?.message || 'Failed to load PDF');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!page || !page.catalog_pdf) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <h1>Catalog Not Available</h1>
        <p style={{ color: 'var(--text-secondary)', margin: '20px 0' }}>
          This page has no catalog PDF.
        </p>
        <Link to="/" className="btn btn-primary">Back to Home</Link>
      </div>
    );
  }

  const totalSpreads = numPages ? Math.ceil(numPages / 2) : 0;
  const leftPageNum = spreadIndex * 2 + 1;
  const rightPageNum = spreadIndex * 2 + 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <div style={{
        padding: '16px 20px',
        backgroundColor: 'var(--background-secondary)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Link to={`/page/${page.slug}`} style={{ color: 'var(--text-secondary)' }}>
          ← Back to {page.title}
        </Link>
        <h1 style={{ fontSize: '1.25rem', margin: 0 }}>{page.title} – Catalog</h1>
        {numPages != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={spreadIndex <= 0}
              onClick={() => setSpreadIndex(i => Math.max(0, i - 1))}
            >
              Previous
            </button>
            <span style={{ color: 'var(--text-secondary)' }}>
              Spread {spreadIndex + 1} of {totalSpreads}
            </span>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              disabled={spreadIndex >= totalSpreads - 1}
              onClick={() => setSpreadIndex(i => Math.min(totalSpreads - 1, i + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '24px',
        backgroundColor: 'var(--background)',
        minHeight: 'calc(100vh - 60px)'
      }}>
        {pdfError ? (
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
            <p>Could not load PDF. You can try opening it in a new tab.</p>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ marginTop: '16px' }}>
              Open PDF in new tab
            </a>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
            borderRadius: '4px',
            overflow: 'hidden',
            backgroundColor: '#888'
          }}>
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div style={{ width: '400px', height: '560px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
                  Loading PDF…
                </div>
              }
            >
              <Page
                pageNumber={leftPageNum}
                width={400}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
              {rightPageNum <= numPages && (
                <Page
                  pageNumber={rightPageNum}
                  width={400}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                />
              )}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}

export default CatalogPdfView;
